import React, { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, Search, Filter } from 'lucide-react';
import { useAutomobileCatalogData } from '../../hooks/useAutomobileCatalogData';
import AutomobileBrandLogo from './AutomobileBrandLogo';
import {
  AUTOMOBILE_BODY_TYPE_LABELS,
  AUTOMOBILE_BODY_TYPE_ORDER,
  AUTOMOBILE_BODY_CHIP,
  formatAutomobileCountry,
  type AutomobileBodyType,
  type AutomobileBrand,
} from '../../types/automobile';
import {
  countModelsForBodyFilter,
  filterModelsForBodyFilter,
} from '../../utils/automobileBodyFilter';
import { getAutomobileModelDisplayName, getAutomobileModelKey } from '../../utils/automobileModelDisplay';

interface Props {
  darkMode: boolean;
}

type SortKey = 'name' | 'country' | 'founded' | 'modelCount';
type SortDir = 'asc' | 'desc';

function countrySortLabel(brand: AutomobileBrand): string {
  return formatAutomobileCountry(brand.country);
}

function compareBrands(a: AutomobileBrand, b: AutomobileBrand, key: SortKey, dir: SortDir): number {
  const mul = dir === 'asc' ? 1 : -1;
  if (key === 'name') {
    return mul * a.name.localeCompare(b.name, 'tr', { sensitivity: 'base' });
  }
  if (key === 'country') {
    const ca = countrySortLabel(a);
    const cb = countrySortLabel(b);
    if (!ca && !cb) return a.name.localeCompare(b.name, 'tr', { sensitivity: 'base' });
    if (!ca) return 1;
    if (!cb) return -1;
    const cmp = ca.localeCompare(cb, 'tr', { sensitivity: 'base' });
    if (cmp !== 0) return mul * cmp;
    return a.name.localeCompare(b.name, 'tr', { sensitivity: 'base' });
  }
  if (key === 'modelCount') {
    const diff = a.models.length - b.models.length;
    if (diff !== 0) return mul * diff;
    return a.name.localeCompare(b.name, 'tr', { sensitivity: 'base' });
  }
  if (key === 'founded') {
    const af = a.founded ?? null;
    const bf = b.founded ?? null;
    if (af == null && bf == null) return a.name.localeCompare(b.name, 'tr', { sensitivity: 'base' });
    if (af == null) return 1;
    if (bf == null) return -1;
    if (af !== bf) return mul * (af - bf);
    return a.name.localeCompare(b.name, 'tr', { sensitivity: 'base' });
  }
  return 0;
}

function ModelChips({
  brand,
  darkMode,
  activeBody,
}: {
  brand: AutomobileBrand;
  darkMode: boolean;
  activeBody: AutomobileBodyType | '';
}) {
  if (brand.models.length === 0) {
    return (
      <span className={darkMode ? 'text-gray-500 text-sm' : 'text-gray-400 text-sm'}>—</span>
    );
  }

  const models = filterModelsForBodyFilter(brand.models, activeBody);

  if (models.length === 0) {
    return (
      <span className={darkMode ? 'text-gray-500 text-sm' : 'text-gray-400 text-sm'}>—</span>
    );
  }

  // Anasayfada: aynı modelin nesil/yıl varyantlarını tek satıra basmayalım.
  // "A3 Sedan (8YS) (04.2020→)" yerine sadece "A3" / "A3 Sedan" gibi aile adını tek kez göster.
  // (Şimdilik familyKey = model.name; varyantlar marka sayfasında detaylı görünür.)
  const familyModels: { name: string; bodyType: AutomobileBodyType }[] = [];
  const seenFamily = new Set<string>();
  for (const m of models) {
    const key = m.name.trim().toLowerCase();
    if (!key || seenFamily.has(key)) continue;
    seenFamily.add(key);
    familyModels.push({ name: m.name, bodyType: m.bodyType });
  }

  const MAX_CHIPS = 12;
  const visible = familyModels.slice(0, MAX_CHIPS);
  const remaining = familyModels.length - visible.length;

  return (
    <div className="flex flex-wrap gap-1.5">
      {visible.map((model, idx) => {
        const label = model.name;
        const showBody =
          label === model.name && familyModels.length < 6;
        return (
        <span
          key={`${brand.slug}|family|${model.name}|${idx}`}
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-xs max-w-full ${AUTOMOBILE_BODY_CHIP[model.bodyType]}`}
          title={`${label} · ${AUTOMOBILE_BODY_TYPE_LABELS[model.bodyType]}`}
        >
          <span className="font-medium truncate">{label}</span>
          {showBody && (
            <span className="opacity-70 shrink-0">· {AUTOMOBILE_BODY_TYPE_LABELS[model.bodyType]}</span>
          )}
        </span>
        );
      })}
      {remaining > 0 && (
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded-md border text-xs ${
            darkMode ? 'border-gray-600 text-gray-300' : 'border-gray-200 text-gray-600'
          }`}
          title={`+${remaining} model daha (detaylar marka sayfasında)`}
        >
          +{remaining}
        </span>
      )}
    </div>
  );
}

const AutomobileBrandsTable: React.FC<Props> = ({ darkMode }) => {
  const { catalog: data, loading } = useAutomobileCatalogData();
  const isLoading = loading && data.brandCount === 0;
  const [search, setSearch] = useState('');
  const [bodyFilter, setBodyFilter] = useState<AutomobileBodyType | ''>('');
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = data.brands.filter((brand) => {
      const visible = filterModelsForBodyFilter(brand.models, bodyFilter);
      if (!visible.length) return false;
      if (!q) return true;
      const searchPool = bodyFilter ? visible : brand.models;
      return (
        brand.name.toLowerCase().includes(q) ||
        brand.slug.includes(q) ||
        searchPool.some((m) => m.name.toLowerCase().includes(q))
      );
    });
    return [...list].sort((a, b) => {
      if (sortKey === 'modelCount') {
        const mul = sortDir === 'asc' ? 1 : -1;
        const diff =
          countModelsForBodyFilter(a.models, bodyFilter) -
          countModelsForBodyFilter(b.models, bodyFilter);
        if (diff !== 0) return mul * diff;
        return a.name.localeCompare(b.name, 'tr', { sensitivity: 'base' });
      }
      return compareBrands(a, b, sortKey, sortDir);
    });
  }, [data.brands, search, bodyFilter, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'name' ? 'asc' : 'desc');
    }
  };

  const sortBtn = (key: SortKey, label: string) => {
    const active = sortKey === key;
    return (
      <button
        type="button"
        onClick={() => toggleSort(key)}
        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border text-xs font-medium transition-colors ${
          active
            ? 'bg-indigo-600 text-white border-indigo-600'
            : darkMode
              ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
              : 'border-gray-300 text-gray-700 hover:bg-gray-50'
        }`}
      >
        {label}
        {active ? (
          sortDir === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
        ) : (
          <ArrowUpDown size={14} className="opacity-50" />
        )}
      </button>
    );
  };

  const SortableTh = ({
    label,
    sortKey: key,
    className = '',
  }: {
    label: string;
    sortKey: SortKey;
    className?: string;
  }) => {
    const active = sortKey === key;
    return (
      <th className={`text-left font-medium px-4 py-3 ${className}`}>
        <button
          type="button"
          onClick={() => toggleSort(key)}
          className={`inline-flex items-center gap-1.5 hover:text-indigo-500 transition-colors ${
            active ? 'text-indigo-500' : ''
          }`}
        >
          {label}
          {active ? (
            sortDir === 'asc' ? (
              <ArrowUp size={14} aria-hidden />
            ) : (
              <ArrowDown size={14} aria-hidden />
            )
          ) : (
            <ArrowUpDown size={14} className="opacity-40" aria-hidden />
          )}
        </button>
      </th>
    );
  };

  const card = darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200 shadow-sm';
  const text = darkMode ? 'text-gray-300' : 'text-gray-600';
  const heading = darkMode ? 'text-white' : 'text-gray-900';
  const inputClass = `w-full pl-10 pr-3 py-2 rounded-lg border text-sm ${
    darkMode ? 'bg-gray-900 border-gray-600 text-white placeholder:text-gray-500' : 'border-gray-300'
  }`;

  return (
    <div className="space-y-4">
      <div className={`rounded-2xl border p-4 sm:p-5 ${card}`}>
        <div className="flex flex-col lg:flex-row lg:items-end gap-3">
          <div className="flex-1 relative">
            <Search
              size={18}
              className={`absolute left-3 top-1/2 -translate-y-1/2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}
            />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Marka veya model ara…"
              className={inputClass}
              aria-label="Marka veya model ara"
            />
          </div>
        </div>

        <div className="mt-3 flex items-start gap-2">
          <Filter size={16} className={`shrink-0 mt-1.5 ${text}`} aria-hidden />
          <div className="flex flex-wrap gap-1.5">
            <button
              type="button"
              onClick={() => setBodyFilter('')}
              className={`px-2.5 py-1 rounded-lg border text-xs ${
                bodyFilter === ''
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : darkMode
                    ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              Tümü
            </button>
            {AUTOMOBILE_BODY_TYPE_ORDER.map((type) => (
              <button
                key={type}
                type="button"
                onClick={() => setBodyFilter(type)}
                className={`px-2.5 py-1 rounded-lg border text-xs ${
                  bodyFilter === type
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : `${AUTOMOBILE_BODY_CHIP[type]} hover:opacity-90`
                }`}
              >
                {AUTOMOBILE_BODY_TYPE_LABELS[type]}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className={`text-xs font-medium shrink-0 ${text}`}>Sırala:</span>
          {sortBtn('name', 'Marka')}
          {sortBtn('country', 'Ülke')}
          {sortBtn('founded', 'Kuruluş')}
          {sortBtn('modelCount', 'Model sayısı')}
        </div>

        <p className={`mt-3 text-xs ${text}`}>
          {isLoading
            ? 'Katalog yükleniyor…'
            : `${filtered.length} / ${data.brandCount} marka · ${data.modelCount} model · marka adına tıklayarak detay sayfasına gidin`}
        </p>
      </div>

      <div className={`rounded-2xl border overflow-hidden ${card}`}>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[800px] text-sm">
            <thead className={darkMode ? 'bg-gray-900/80 text-gray-300' : 'bg-gray-50 text-gray-600'}>
              <tr>
                <th className="text-left font-medium px-4 py-3 w-[172px] sm:w-[188px]">Logo</th>
                <SortableTh label="Marka" sortKey="name" className="w-44" />
                <SortableTh label="Ülke" sortKey="country" className="w-28" />
                <SortableTh label="Kuruluş" sortKey="founded" className="w-24" />
                <SortableTh label="Model" sortKey="modelCount" className="w-16 text-center" />
                <th className="text-left font-medium px-4 py-3">Modeller · Gövde Tipi</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={6} className={`px-4 py-10 text-center text-sm ${text}`}>
                    Yükleniyor…
                  </td>
                </tr>
              ) : (
                filtered.map((brand) => (
                <tr
                  key={brand.slug}
                  className={`border-t ${darkMode ? 'border-gray-700 hover:bg-gray-700/30' : 'border-gray-100 hover:bg-gray-50/80'}`}
                >
                  <td className="px-3 sm:px-4 py-2 align-middle w-[172px] sm:w-[188px]">
                    <a href={`/otomobil/${encodeURIComponent(brand.slug)}`} className="inline-block align-middle">
                      <AutomobileBrandLogo brand={brand} darkMode={darkMode} />
                    </a>
                  </td>
                  <td className={`px-4 py-3 align-middle font-medium ${heading}`}>
                    <a
                      href={`/otomobil/${encodeURIComponent(brand.slug)}`}
                      className="text-indigo-500 hover:text-indigo-400 hover:underline"
                    >
                      {brand.name}
                    </a>
                  </td>
                  <td className={`px-4 py-3 align-middle ${text}`}>
                    {formatAutomobileCountry(brand.country) || '—'}
                  </td>
                  <td className={`px-4 py-3 align-middle tabular-nums ${text}`}>
                    {brand.founded ? brand.founded : '—'}
                  </td>
                  <td className={`px-4 py-3 align-middle text-center tabular-nums ${text}`}>
                    {countModelsForBodyFilter(brand.models, bodyFilter)}
                  </td>
                  <td className="px-4 py-3 align-top">
                    <ModelChips brand={brand} darkMode={darkMode} activeBody={bodyFilter} />
                  </td>
                </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && !isLoading && (
          <p className={`px-4 py-8 text-center text-sm ${text}`}>Eşleşen marka bulunamadı.</p>
        )}
      </div>
    </div>
  );
};

export default AutomobileBrandsTable;
